import { createTheme, Theme } from '@mui/material/styles';
import { lightPalette, darkPalette } from './palette';
import { typography } from './typography';

/**
 * AI DTR Theme Configuration
 *
 * Features AWS design system colors and modern data visualization aesthetics.
 * Supports light and dark modes via `createAppTheme(mode)`.
 */
export function createAppTheme(mode: 'light' | 'dark' = 'light'): Theme {
  const selectedPalette = mode === 'dark' ? darkPalette : lightPalette;

  return createTheme({
    palette: selectedPalette,
    typography: {
      ...typography,
      h4: {
        fontWeight: 600,
        fontSize: '2.125rem',
      },
      h5: {
        fontWeight: 600,
        fontSize: '1.5rem',
      },
      h6: {
        fontWeight: 500,
        fontSize: '1.25rem',
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.5,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.43,
      },
    },
    components: {
      MuiButton: {
        defaultProps: {
          size: 'medium',
        },
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: 100, // Pill shape
            transition: 'all 0.2s ease-in-out',
            padding: '8px 20px',
          },
          sizeSmall: {
            padding: '4px 12px',
            fontSize: '0.8125rem',
          },
          sizeMedium: {
            padding: '8px 20px',
            fontSize: '0.9375rem',
          },
          sizeLarge: {
            padding: '12px 28px',
            fontSize: '1rem',
          },
          contained: {
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            '&:hover': {
              boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
              transform: 'translateY(-1px)',
            },
          },
          outlined: {
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: '50%',
            transition: 'all 0.2s ease-in-out',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: 'rgba(255, 255, 255, 0.92)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            transition: 'all 0.2s ease-in-out',
          },
          elevation1: {
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          },
          elevation2: {
            boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: 'rgba(255, 255, 255, 0.92)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              backgroundColor: 'rgba(255, 255, 255, 1)',
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            minHeight: 48,
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 500,
            transition: 'all 0.2s ease',
          },
          clickable: {
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
              transition: 'all 0.2s ease',
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            height: 8,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: '1px solid #e0e0e0',
            boxShadow: '2px 0 8px rgba(0, 0, 0, 0.05)',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: '#f5f5f5',
              transition: 'background-color 0.2s ease',
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.08)',
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            fontSize: '0.875rem',
            padding: '8px 12px',
            borderRadius: 4,
          },
        },
      },
    },
    shape: {
      borderRadius: 16, // Rounder cards
    },
    spacing: 8,
  });
}

// Backward compatibility
export const theme = createAppTheme('light');
